/**
 * Страница списка заказов швейного цеха.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import {
  Button,
  DataTable,
  Paper,
  ConfirmModal,
  MenuButton,
  PaginationControlled,
  CollapsibleListFilters,
  FilterFieldLabel,
  FiltersPanel,
  FilterTextInput,
} from '../uikit';
import type { IDataTableColumn, ISortedTable, OrderDirectionType } from '../uikit';
import type { OrderListItemDTO } from '../api/orders';
import {
  findOrders,
  deleteOrders,
  ORDER_STATUS_LABELS,
  type FindOrdersParams,
} from '../api/orders';
import { toUserMessage } from '../api/client';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { formatDate } from '../utils/formatDate';
import { formatInteger } from '../utils/formatNumber';
import { IconEdit, IconDelete } from '../uikit/components/icons';

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin: 0 -${({ theme }) => theme.space[2]}px 12px;
  padding-left: ${({ theme }) => theme.space[2]}px;
  padding-right: ${({ theme }) => theme.space[2]}px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  line-height: 1.5;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ErrorRow = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  ${({ theme }) => theme.device.mobile} {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const PageLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const TableSection = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

function getOrderColumns(
  onEdit: (item: OrderListItemDTO) => void,
  onDelete: (item: OrderListItemDTO) => void,
): IDataTableColumn<OrderListItemDTO>[] {
  return [
    {
      key: 'title',
      header: 'Название',
      sortable: true,
      fixedWidth: 200,
      cellStyle: { px: 2 },
      render: (value, item) => {
        const name = (value as string) || '—';
        return (
          <Link
            to={`/orders/${item.id}`}
            style={{
              color: 'inherit',
              textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            {name}
          </Link>
        );
      },
    },
    {
      key: 'customer',
      header: 'Заказчик',
      sortable: true,
      fixedWidth: 180,
      cellStyle: { px: 2 },
      render: (value) => (value ? String(value) : '—'),
    },
    {
      key: 'date',
      header: 'Дата',
      sortable: true,
      fixedWidth: 120,
      cellStyle: { px: 2 },
      render: (value) => formatDate(value as string | null),
    },
    {
      key: 'status',
      header: 'Статус',
      sortable: true,
      fixedWidth: 120,
      cellStyle: { px: 2 },
      render: (value) => {
        const status = value as string;
        return status ? (ORDER_STATUS_LABELS[status as keyof typeof ORDER_STATUS_LABELS] ?? status) : '—';
      },
    },
    {
      key: 'quantity',
      header: { text: 'Кол-во, шт', headerCellStyle: { textAlign: 'right' } },
      sortable: true,
      fixedWidth: 80,
      cellStyle: { px: 2, textAlign: 'right' },
      render: (value) => formatInteger(value as number | null),
    },
    {
      key: 'actions',
      header: 'Действия',
      sortable: false,
      fixedWidth: 84,
      truncate: false,
      cellStyle: { px: 1, textAlign: 'right' },
      render: (_, item) => (
        <div style={{ display: 'flex', gap: 0, justifyContent: 'flex-end' }}>
          <MenuButton
            size={28}
            aria-label="Редактировать"
            onClick={() => onEdit(item)}
          >
            <IconEdit />
          </MenuButton>
          <MenuButton
            size={28}
            aria-label="Удалить"
            onClick={() => onDelete(item)}
          >
            <IconDelete />
          </MenuButton>
        </div>
      ),
    },
  ];
}

export function OrdersListPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [items, setItems] = useState<OrderListItemDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<OrderListItemDTO | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [sort, setSort] = useState<ISortedTable>({ orderBy: 'title', orderDirection: 'ASC' });
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [titleInput, setTitleInput] = useState('');
  const [customerInput, setCustomerInput] = useState('');

  const debouncedTitle = useDebouncedValue(titleInput, 300);
  const debouncedCustomer = useDebouncedValue(customerInput, 300);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params: FindOrdersParams = {
        page,
        itemsPerPage,
        title: debouncedTitle || undefined,
        customer: debouncedCustomer || undefined,
        order: [{ field: sort.orderBy ?? 'title', direction: sort.orderDirection ?? 'ASC' }],
      };
      const result = await findOrders(params);
      setItems(result.items);
      setTotal(result.count);
    } catch (err) {
      setItems([]);
      setTotal(0);
      setLoadError(toUserMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page, itemsPerPage, debouncedTitle, debouncedCustomer, sort.orderBy, sort.orderDirection]);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  const handleSort = (key: string, direction: OrderDirectionType) => {
    setSort({ orderBy: key, orderDirection: direction });
    setPage(1);
  };

  const handleEditClick = (order: OrderListItemDTO) => {
    navigate(`/orders/${order.id}/edit`);
  };

  const handleDeleteClick = (order: OrderListItemDTO) => {
    setDeleteTarget(order);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    setLoadError(null);
    try {
      await deleteOrders([deleteTarget.id]);
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      await loadOrders();
    } catch (err) {
      setLoadError(toUserMessage(err));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <Paper padding={16} shadow={0} style={{ marginBottom: 0, height: '100%' }}>
        <PageLayout>
          <HeaderRow>
            <PageTitle>Заказы</PageTitle>
            <Button
              variant="filled"
              color="primary"
              size="s"
              onClick={() => navigate('/orders/new')}
            >
              Создать
            </Button>
          </HeaderRow>
          <CollapsibleListFilters
            activeFilterCount={
              (titleInput.trim() !== '' ? 1 : 0) + (customerInput.trim() !== '' ? 1 : 0)
            }
          >
            <FiltersPanel $borderless>
            <FilterFieldLabel htmlFor="order-title-filter">
              Название:
              <FilterTextInput
                id="order-title-filter"
                type="text"
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
                placeholder="Поиск по названию"
                aria-label="Поиск по названию заказа"
                $maxWidthPx={220}
              />
            </FilterFieldLabel>
            <FilterFieldLabel htmlFor="order-customer-filter">
              Заказчик:
              <FilterTextInput
                id="order-customer-filter"
                type="text"
                value={customerInput}
                onChange={(e) => setCustomerInput(e.target.value)}
                placeholder="Поиск по заказчику"
                aria-label="Поиск по заказчику"
                $maxWidthPx={220}
              />
            </FilterFieldLabel>
            </FiltersPanel>
          </CollapsibleListFilters>
          <TableSection>
            <DataTable
              data={items}
              columns={getOrderColumns(handleEditClick, handleDeleteClick)}
              orderBy={sort.orderBy}
              orderDirection={sort.orderDirection}
              onSort={handleSort}
              loading={loading}
              settings={{ horizontalLine: true, backlightLine: true }}
              minHeight={0}
              height="100%"
              placeholder={{ text: 'Заказы не найдены' }}
            />
          </TableSection>
          {loadError && (
            <ErrorRow role="alert">
              <span style={{ color: theme.colors.error.main, fontSize: theme.fontSizes[5] }}>{loadError}</span>
              <Button variant="outlined" color="primary" size="s" onClick={() => void loadOrders()}>
                Повторить
              </Button>
            </ErrorRow>
          )}
          <PaginationControlled
            page={page}
            itemsPerPage={itemsPerPage}
            totalItems={total}
            setPage={setPage}
            setItemsPerPage={setItemsPerPage}
          />
        </PageLayout>
      </Paper>
      <ConfirmModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Удалить заказ?"
        message={
          deleteTarget ? (
            <>Вы уверены, что хотите удалить заказ «{deleteTarget.title}»?</>
          ) : undefined
        }
        confirmText="Удалить"
        cancelText="Отмена"
        confirmColor="error"
        loading={deleteLoading}
      />
    </>
  );
}
